/**
 * src/game/progression/battle-campaign-progress.ts —
 * 戰鬥端「闖關地圖」進度推算 + 過關標記 + 配件解鎖(純邏輯,可單測).
 *
 * 雙軌進度鐵則(CLAUDE.md 最高優先 / spec §7 紅線):
 *   闖關屬「玩遊戲軌(廣度)」。本模組**只**寫廣度欄位:
 *     - `battleCampaign.clearedStages` / `battleCampaign.stageStars`
 *     - `unlockedAccessories`
 *   **絕不**碰 factionXP / freeXP / skillLevels / 傷害 / HP / 冷卻 /
 *   commanderXp(指揮官 XP 由 result-scene 的 addCommanderXp 處理,本模組不重複)。
 *
 * 學習/遊戲分離鐵則:`battleCampaign`(戰鬥端)與學習端 `campaignState`
 *   **物理獨立**,本檔絕不讀寫 campaignState(避免雙寫漂移,研究文件注意點)。
 *
 * 解鎖採「線性推算」:第 1 關預設解鎖;通關第 n 關 → 解鎖第 n+1 關。
 *   **不另存** unlocked 清單 —— unlocked 永遠由 clearedStages 即時推導,
 *   single source of truth 為 clearedStages,避免兩份資料漂移。
 *
 * mutate-in-place 風格對齊 shared/progress.ts:addFactionXP / commander.ts:
 *   addCommanderXp —— caller 負責 saveProgress(p)。
 */
import type { PlayerProgress } from '../../shared/types';
import {
  BATTLE_CAMPAIGN_STAGES,
  STAGE_CLEAR_ACCESSORY_UNLOCKS,
  campaignChapters,
  getStageDef,
  stageIndex,
} from '../data/battle-campaign';

/** 星等下限 / 上限。 */
export const MIN_STARS = 1;
export const MAX_STARS = 3;

/**
 * 確保 `battleCampaign` 物件存在且形狀正確(legacy / 損壞存檔的防禦)。
 * mutate-in-place 並回傳同一參考。
 */
function ensureBattleCampaign(
  p: PlayerProgress,
): { clearedStages: string[]; stageStars: Record<string, number> } {
  const bc = p.battleCampaign;
  if (
    !bc
    || typeof bc !== 'object'
    || !Array.isArray((bc as { clearedStages?: unknown }).clearedStages)
    || typeof (bc as { stageStars?: unknown }).stageStars !== 'object'
    || (bc as { stageStars?: unknown }).stageStars === null
  ) {
    p.battleCampaign = { clearedStages: [], stageStars: {} };
  }
  return p.battleCampaign as { clearedStages: string[]; stageStars: Record<string, number> };
}

/** 某關是否已通關。 */
export function isStageCleared(p: PlayerProgress, stageId: string): boolean {
  const bc = p.battleCampaign;
  return Array.isArray(bc?.clearedStages) && bc!.clearedStages.includes(stageId);
}

/** 某關目前的星等(0 = 未通關 / 無紀錄)。 */
export function getStageStars(p: PlayerProgress, stageId: string): number {
  const bc = p.battleCampaign;
  const v = bc?.stageStars?.[stageId];
  return typeof v === 'number' && v > 0 ? v : 0;
}

/**
 * 某關是否「已解鎖」(可點)。線性規則:
 *   - 線性序列第 1 關永遠解鎖。
 *   - 其餘關卡:前一關(order-1)已通關才解鎖。
 *   - 已通關的關卡當然視為已解鎖(可重打刷星)。
 * 由 clearedStages 即時推導,不另存 unlocked。
 */
export function isStageUnlocked(p: PlayerProgress, stageId: string): boolean {
  const idx = stageIndex(stageId);
  if (idx < 0) return false;
  if (idx === 0) return true; // 線性主幹首關預設解鎖
  if (isStageCleared(p, stageId)) return true; // 打過的當然能再進
  const prev = BATTLE_CAMPAIGN_STAGES[idx - 1];
  return isStageCleared(p, prev.id);
}

/**
 * 目前「最前緣可挑戰但尚未通關」的關卡 id 清單(線性下通常恰 1 個)。
 * 給地圖場景做「下一關高亮」用。全部通關 → 回空陣列。
 */
export function nextUnlockableStageIds(p: PlayerProgress): string[] {
  const out: string[] = [];
  for (const s of BATTLE_CAMPAIGN_STAGES) {
    if (isStageUnlocked(p, s.id) && !isStageCleared(p, s.id)) out.push(s.id);
  }
  return out;
}

/**
 * 玩家打開地圖時預設顯示的章節(R-004 hotfix:地圖改分頁後,要自動翻到
 * 「進度最前緣」那一章)。規則:第一個「已解鎖且未通關」關卡所屬章;
 * 全部通關 → 最後一章;progress 缺/壞 → 第 1 章。純函式可單元測試。
 */
export function defaultChapterFor(p: PlayerProgress | null | undefined): number {
  const chapters = campaignChapters();
  const last = chapters[chapters.length - 1] ?? 1;
  if (!p) return chapters[0] ?? 1;
  for (const s of BATTLE_CAMPAIGN_STAGES) {
    if (isStageUnlocked(p, s.id) && !isStageCleared(p, s.id)) return s.chapter;
  }
  return last;
}

/**
 * 依「勝利時剩餘血量比例」評星(1~3,K-3 友善的簡單準則):
 *   - hpRatio >= 0.8(幾乎無傷)→ 3 星
 *   - hpRatio >= 0.4(受了點傷)→ 2 星
 *   - 其餘(只要通關)        → 1 星
 * hpRatio 應為 0..1;NaN / 負 → 視為 0(最低 1 星,因為「有通關」)。
 *
 * 鐵則:星等純「表現度量」,只進 stageStars(廣度/展示),不換任何戰力。
 */
export function computeStars(hpRatio: number): number {
  const r = Number.isFinite(hpRatio) ? Math.max(0, Math.min(1, hpRatio)) : 0;
  if (r >= 0.8) return 3;
  if (r >= 0.4) return 2;
  return MIN_STARS;
}

export interface StageClearResult {
  /** 是否「新」通關(之前未在 clearedStages)。 */
  newlyCleared: boolean;
  /** 本次寫入的星等(1~3)。若刷出更高星,取較高者。 */
  stars: number;
  /** 是否刷新了更高星(舊星 < 新星)。 */
  improvedStars: boolean;
  /** 本次因通關(boss 關)新解鎖的配件 id(只列實際新增者)。 */
  unlockedAccessories: string[];
  /** 線性推算下「本次因此關通關而新解鎖」的下一關 id(無則 null)。 */
  newlyUnlockedStageId: string | null;
}

/**
 * 標記某關通關 + 評星 + (boss 關)解鎖配件,mutate-in-place。
 * caller 負責 saveProgress(p)。
 *
 * @param stageId  通關的關卡 id(須存在於 BATTLE_CAMPAIGN_STAGES)。
 * @param hpRatio  勝利時玩家剩餘血量比例(0..1),用於評星。
 *
 * 鐵則:本函式**只**寫 battleCampaign + unlockedAccessories,絕不碰戰力欄位。
 */
export function markStageCleared(
  p: PlayerProgress,
  stageId: string,
  hpRatio: number,
): StageClearResult {
  const def = getStageDef(stageId);
  const result: StageClearResult = {
    newlyCleared: false,
    stars: 0,
    improvedStars: false,
    unlockedAccessories: [],
    newlyUnlockedStageId: null,
  };
  // 未知關卡 id → no-op(防呆,不污染存檔)。
  if (!def) return result;

  const bc = ensureBattleCampaign(p);
  const wasCleared = bc.clearedStages.includes(stageId);
  const prevStars = getStageStars(p, stageId);
  const newStars = computeStars(hpRatio);
  // 取較高星(重打刷新只升不降)。
  const finalStars = Math.max(prevStars, newStars);

  if (!wasCleared) {
    bc.clearedStages.push(stageId);
    result.newlyCleared = true;
  }
  bc.stageStars[stageId] = finalStars;
  result.stars = finalStars;
  result.improvedStars = finalStars > prevStars;

  // boss 關通關 → 解鎖配件(永久廣度)。冪等,不重複加。
  if (def.isBoss) {
    const toUnlock = STAGE_CLEAR_ACCESSORY_UNLOCKS[stageId];
    if (toUnlock && toUnlock.length > 0) {
      if (!Array.isArray(p.unlockedAccessories)) p.unlockedAccessories = [];
      for (const acc of toUnlock) {
        if (!p.unlockedAccessories.includes(acc)) {
          p.unlockedAccessories.push(acc);
          result.unlockedAccessories.push(acc);
        }
      }
    }
  }

  // 線性推算:此關通關後「新解鎖」的下一關(若存在且先前未解鎖)。
  const idx = stageIndex(stageId);
  const next = idx >= 0 ? BATTLE_CAMPAIGN_STAGES[idx + 1] : undefined;
  if (next && result.newlyCleared) {
    result.newlyUnlockedStageId = next.id;
  }

  return result;
}
